import { apiCall, API_BASE_URL } from '../../../utils/apiClient';
import type { ForceApproveResult, ForceApproveWarning } from './types';

interface ForceApproveWarningDto {
  code?: string;
  field?: string;
  message?: string;
  severity?: string;
}

interface ForceApproveResponseDto {
  hasIssues?: boolean;
  has_issues?: boolean;

  approved?: boolean;

  warnings?: ForceApproveWarningDto[];

  approvedInstanceId?: string | number;
  approved_instance_id?: string | number;

  checkedInstanceId?: string | number;
  checked_instance_id?: string | number;
}

async function throwForFailedResponse(
  response: Response,
  action: string,
): Promise<void> {
  if (response.ok) {
    return;
  }

  const errorText = await response.text();

  throw new Error(`${action} failed: ${response.status} ${errorText}`);
}

function mapWarning(warning: ForceApproveWarningDto): ForceApproveWarning {
  return {
    code: warning.code ?? 'UNKNOWN_WARNING',
    field: warning.field ?? '',
    message: warning.message ?? '',
    severity: warning.severity?.toLowerCase() === 'error' ? 'error' : 'warning',
  };
}

export async function startTestCaseThread(threadId: string): Promise<void> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(threadId)}/start`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  await throwForFailedResponse(response, 'Start working');
}

export async function approveTestCaseThread(threadId: string): Promise<void> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(threadId)}/approve`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  await throwForFailedResponse(response, 'Approve test case thread');
}

export async function forceApproveTestCaseThread(
  threadId: string,
  expectedInstanceId?: string,
): Promise<ForceApproveResult> {
  const query = new URLSearchParams();

  if (expectedInstanceId !== undefined) {
    const numericExpectedInstanceId = Number(expectedInstanceId);

    if (
      !Number.isInteger(numericExpectedInstanceId) ||
      numericExpectedInstanceId <= 0
    ) {
      throw new Error('Force approval requires a valid expected instance ID.');
    }

    query.set('confirm', 'true');
    query.set('expected_instance_id', String(numericExpectedInstanceId));
  }

  const queryString = query.toString();

  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(threadId)}/force-approve${
      queryString ? `?${queryString}` : ''
    }`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  await throwForFailedResponse(response, 'Force approve test case thread');

  const text = await response.text();

  if (!text) {
    throw new Error(
      'Force approve test case thread returned an empty response.',
    );
  }

  const data = JSON.parse(text) as ForceApproveResponseDto;

  const warnings = (data.warnings ?? []).map(mapWarning);

  const hasIssues = data.hasIssues ?? data.has_issues ?? warnings.length > 0;

  const rawCheckedInstanceId =
    data.checkedInstanceId ?? data.checked_instance_id;

  const checkedInstanceId =
    rawCheckedInstanceId === undefined || rawCheckedInstanceId === null
      ? ''
      : String(rawCheckedInstanceId);

  if (!data.approved && hasIssues && !checkedInstanceId) {
    throw new Error(
      'Force approval response did not contain checked_instance_id.',
    );
  }

  return {
    approved: data.approved === true,
    warnings,
    checkedInstanceId,
  };
}

export async function deleteTestCaseThread(threadId: string): Promise<void> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(threadId)}`,
    {
      method: 'DELETE',
    },
  );

  await throwForFailedResponse(response, 'Delete test case thread');
}
